import { FieldProps, FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
import { TimeInput, toMoment } from '@core/utils/time';
import FormHelperText from '@material-ui/core/FormHelperText';
import moment from 'moment';
import React, { Fragment, useCallback, useState } from 'react';
import { DateRangePicker, FocusedInputShape, SingleDatePickerShape } from 'react-dates';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { FormGroup, Label } from 'reactstrap';

export interface FormWeekDateRangeInputProps extends FieldProps<FormControl<string> | FormControl<string | null>> {
  options?: SingleDatePickerShape;
  minDate?: TimeInput;
  maxDate?: TimeInput;
  onChange?(range: string): void;
  appendToBody?: boolean;
  anchorDirection?: 'left' | 'right';
  withoutGroup?: boolean;
  small?: boolean;
  isClearable?: boolean;
}

export function FormWeekDateRange(props: FormWeekDateRangeInputProps) {
  const { formControl, fieldState, fieldConfig } = useFormField(props);

  const fieldValue = fieldState.value;
  const sStart = R.toNumber(R.toString(fieldValue).split('-')[0]);
  const sEnd = R.toNumber(R.toString(fieldValue).split('-')[1]);

  const startDate = sStart ? toMoment(sStart).startOf('isoWeek') : toMoment().startOf('isoWeek');
  const endDate = sEnd ? toMoment(sEnd).endOf('isoWeek') : toMoment().endOf('isoWeek');

  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(null);

  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;

  const fieldError = (fieldTouched && fieldState.errorMessage) || null;

  const fieldReadonly = fieldConfig.readonly || false;
  const fieldDisabled = fieldConfig.disabled || false;

  const fieldPlaceholder = fieldConfig.placeholder || 'mm/dd/yyyy';

  const handleChange = useCallback(
    (dateClicked: moment.Moment | null, endDate: moment.Moment | null) => {
      const start = dateClicked?.startOf('isoWeek').unix();
      const end = dateClicked?.endOf('isoWeek').unix();

      const fieldValue = `${start}-${end}`;
      formControl.setValue(fieldValue);
      props.onChange && props.onChange(fieldValue);
    },
    [formControl, props],
  );

  const content = (
    <Fragment>
      {fieldConfig.label && (
        <>
          <Label for={formControl.uid}>{fieldConfig.label}</Label> <br />
        </>
      )}
      <DateRangePicker
        small={props.small}
        readOnly
        numberOfMonths={1}
        startDate={startDate}
        startDateOffset={startDate => startDate}
        endDateOffset={endDate => endDate}
        startDateId={`${formControl.uid}-start`}
        endDate={endDate}
        endDateId={`${formControl.uid}-end`}
        onDatesChange={({ startDate, endDate }) => handleChange(startDate, endDate)}
        focusedInput={focusedInput}
        onFocusChange={focusedInput => setFocusedInput(focusedInput)}
        isOutsideRange={() => false}
        // readOnly={fieldReadonly}
        disabled={fieldDisabled}
        startDatePlaceholderText={fieldPlaceholder}
        endDatePlaceholderText={fieldPlaceholder}
        appendToBody={props.appendToBody}
        minimumNights={7}
        showClearDates={props.isClearable}
      />
      {!!fieldError && <FormHelperText style={{ color: '#f44336' }}>{fieldError}</FormHelperText>}
    </Fragment>
  );

  if (props.withoutGroup) {
    return <div className="theme_date-picker">{content}</div>;
  }

  return <FormGroup className="theme_date-picker">{content}</FormGroup>;
}
